import { useEffect, useMemo, useRef, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";

import QRCodeStyling from "qr-code-styling";
import { Button, Icon, Label, Message, Modal } from "semantic-ui-react";

import { Input } from "simplydo/react/component-library";

import actions from "actions";
import api from "api";
import AppIcon from "src/images/appIcon.png";
import { useAppDispatch } from "store";

export const SuggestedMFAApps = () => {
  const { t } = useTranslation();

  return (
    <p>
      {t("entry.mfa.suggestedApps")}
      Microsoft Authenticator (
      <a
        href="https://apps.apple.com/us/app/microsoft-authenticator/id983156458"
        target="_blank"
        rel="noopener noreferrer"
      >
        iOS
      </a>{" "}
      /{" "}
      <a
        href="https://play.google.com/store/apps/details?id=com.azure.authenticator"
        target="_blank"
        rel="noopener noreferrer"
      >
        Android
      </a>
      ) {t("generic.and")} Google Authenticator (
      <a
        href="https://apps.apple.com/us/app/google-authenticator/id388497605"
        target="_blank"
        rel="noopener noreferrer"
      >
        iOS
      </a>{" "}
      /{" "}
      <a
        href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
        target="_blank"
        rel="noopener noreferrer"
      >
        Android
      </a>
      )
    </p>
  );
};

export const EnableMfa = ({
  open,
  onClose,
  onEnableMfa,
}: {
  open: boolean;
  onClose: () => void;
  onEnableMfa: () => void;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [otpVerifier, setOtpVerifier] = useState("");
  const otpQrRef = useRef(null);
  const [mfaLoading, setMfaLoading] = useState(false);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  const [uri, setUri] = useState<string | null>(null);
  const [recoveryCodesModalOpen, setRecoveryCodesModalOpen] = useState(false);

  useEffect(() => {
    if (open) {
      api.auth.generateOtpUri(
        (check) => {
          setUri(check.uri);
        },
        (err) => {
          toast.error(err.message);
        },
      );
    }
  }, [open]);

  const qrCode = useMemo(
    () =>
      new QRCodeStyling({
        width: 200,
        height: 200,
        type: "svg",
        data: uri,
        image: AppIcon,
        dotsOptions: { color: "#14435B" },
        backgroundOptions: { color: "#ffffff" },
        imageOptions: { margin: 4 },
      }),
    [uri],
  );

  useEffect(() => {
    if (open && !qrCode._container) {
      qrCode.append(otpQrRef.current);
    }
  }, [open, qrCode]);

  const downloadCodes = () => {
    const codes = recoveryCodes.join(", ");
    const blob = new Blob([codes], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "mfa-recovery-codes.txt";
    a.click();
  };

  const copyCodes = () => {
    const codes = recoveryCodes.join(", ");
    navigator.clipboard.writeText(codes);
    toast.success("Codes copied to clipboard");
  };

  const checkMfaCode = () => {
    const loadingId = toast.loading(t("entry.mfa.statusMessageLoading"));
    setMfaLoading(true);
    api.auth.enableOtp(
      { otp: otpVerifier },
      ({ recoveryCodes }) => {
        setMfaLoading(false);
        onEnableMfa?.();
        onClose();
        setRecoveryCodes(recoveryCodes);
        setOtpVerifier("");
        toast.success(t("entry.mfa.statusMessageSuccess"), { id: loadingId });
        setRecoveryCodesModalOpen(true);
      },
      (err) => {
        setMfaLoading(false);
        toast.error(err.message, { id: loadingId });
        setOtpVerifier("");
      },
    );
  };

  return (
    <>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={open}
      >
        <Modal.Header>
          <Icon name="lock" style={{ paddingRight: "5px" }} />
          {t("entry.mfa.enableAction")}
        </Modal.Header>
        <Modal.Content scrolling>
          <h3>{t("generic.step")} 1</h3>
          <p>{t("entry.mfa.step1")}</p>
          <Message info>
            <SuggestedMFAApps />
          </Message>
          <h3>{t("generic.step")} 2</h3>
          <p>{t("entry.mfa.step2")}</p>
          <div
            style={{ width: "70%", margin: "10px auto", textAlign: "center" }}
            ref={otpQrRef}
          />
          <h3>{t("generic.step")} 3</h3>
          <p>{t("entry.mfa.step3")}</p>
          <div style={{ textAlign: "center" }}>
            <Input
              onKeyDown={(e) => {
                e.keyCode === 13 && checkMfaCode();
              }}
              size="large"
              onChange={(e) => setOtpVerifier(e.target.value)}
              value={otpVerifier}
              placeholder="123456"
              autoFocus
            />
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button
            content={t("generic.cancel")}
            onClick={() => {
              onClose();
            }}
          />
          <Button
            primary
            content={t("generic.verify")}
            loading={mfaLoading}
            onClick={() => checkMfaCode()}
            disabled={otpVerifier.replaceAll(" ", "").length !== 6}
          />
        </Modal.Actions>
      </Modal>
      <Modal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={recoveryCodesModalOpen}
      >
        <Modal.Header>Multi-factor authentication recovery codes</Modal.Header>
        <Modal.Content>
          <p>
            These recovery codes can be used to access your account in the event
            you cannot access your authentication device. Keep these codes safe
            and do not share them with anyone. We recommend storing them
            securely in your password manager.
          </p>
          <p>
            It is important to save your codes and keep them safe so you don't
            lose access to your account.
          </p>
          <h5>Your codes are:</h5>
          <div
            style={{
              marginBottom: "20px",
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
            }}
          >
            {recoveryCodes.map((code, index) => (
              <Label key={index} content={code} />
            ))}
          </div>
          <Button
            size="small"
            icon="download"
            content="Download codes"
            onClick={() => downloadCodes()}
          />
          <Button
            size="small"
            icon="copy"
            content="Copy codes"
            onClick={() => copyCodes()}
          />
        </Modal.Content>
        <Modal.Actions>
          <Button
            primary
            content="I have saved my codes"
            onClick={() => {
              dispatch(actions.user.enableMfa());
              setRecoveryCodesModalOpen(false);
            }}
          />
        </Modal.Actions>
      </Modal>
    </>
  );
};
